import { RRule, Weekday as RRuleWeekday } from "rrule";
import { getEffectiveRecurrence } from "@/lib/recurrence";
import { utcDayBounds, utcDayKey } from "@/lib/calendarDay";
import type { Booking } from "@/types/Booking";

// R15 (hearth/docs/BOOKING_RULES.md#r15) — A recurring booking is "active" if it has at
// least one non-excluded occurrence today or in the future. Single (non-recurring) bookings
// are active if their date is today or in the future. Members can edit active bookings only;
// elevated users can always edit regardless (see R21).

const WEEKDAY_MAP: Record<string, RRuleWeekday> = {
  MO: RRule.MO,
  TU: RRule.TU,
  WE: RRule.WE,
  TH: RRule.TH,
  FR: RRule.FR,
  SA: RRule.SA,
  SU: RRule.SU,
};

/**
 * Returns true when the booking has at least one occurrence (today or in the future) that is
 * not listed as an exception. Slot bookings (infinite weekly recurrence) are always active
 * until explicitly cancelled.
 *
 * Pass `today` for deterministic testing; defaults to the current date.
 */
export function isRecurringActive(booking: Booking, now: Date = new Date()): boolean {
  if (booking.isCancelled) return false;

  // `now`'s UTC calendar day — the booking's `date` is UTC-midnight of the centre's day, so
  // comparing against the UTC (not viewer-local) start of "today" keeps day-identity correct
  // for a viewer whose browser TZ differs from the centre's.
  const todayStart = utcDayBounds(now).start;
  const bookingDate = booking.date instanceof Date ? booking.date : new Date(booking.date);
  const endHours = parseInt(booking.endTime.slice(0, 2), 10);
  const endMinutes = parseInt(booking.endTime.slice(2, 4), 10);

  // Build the occurrence's end INSTANT on `d`'s UTC calendar day at the booking's end time.
  // `d` is always a UTC-day-anchored Date (booking.date, or an RRule candidate derived from a
  // UTC-midnight dtstart below) — Date.UTC keeps the day fixed regardless of viewer TZ, unlike
  // the previous local `setHours`, which reinterpreted the UTC-midnight day in browser-local time.
  const endOfBookingDay = (d: Date): Date =>
    new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), endHours, endMinutes, 0, 0));

  // Non-recurring: active iff the booking's end-datetime is still in the future. A "today at
  // 07:00–08:00" booking ceases to be active at 08:00, not at midnight.
  // Slot bookings whose `recurrence` document is missing get a synthesised weekly schedule
  // via getEffectiveRecurrence. Without this, isRecurringActive would treat them as
  // non-recurring and report them inactive past their genesis day. See lib/recurrence.ts
  // for the bug write-up.
  const recurrence = getEffectiveRecurrence(booking);
  if (!recurrence) {
    return endOfBookingDay(bookingDate) > now;
  }

  // dtstart is UTC-midnight of the booking's UTC day (not its actual start time). rrule.js reads
  // a Date's UTC getters internally, so this is the only construction that keeps the recurrence's
  // day-of-week/day-of-month math anchored to the correct UTC day in every viewer TZ — see
  // buildRuleForDateCheck in lib/recurrence.ts for the same pattern. The real start/end instants
  // are rebuilt separately (endOfBookingDay) from each candidate's UTC day + the booking's HHmm.
  const dateStr = utcDayKey(bookingDate);
  const dtStart = new Date(dateStr);

  const rule = new RRule({
    freq:
      recurrence.frequency === "daily"
        ? RRule.DAILY
        : recurrence.frequency === "weekly"
          ? RRule.WEEKLY
          : RRule.MONTHLY,
    interval: recurrence.interval ?? 1,
    count: recurrence.count,
    until: recurrence.until ? new Date(recurrence.until) : undefined,
    byweekday: recurrence.byWeekday
      ? recurrence.byWeekday.map((day) => WEEKDAY_MAP[day])
      : undefined,
    bysetpos: recurrence.bySetPos,
    dtstart: dtStart,
  });

  const exceptions = new Set(
    (recurrence.exceptions ?? []).map((d) => utcDayKey(new Date(d))),
  );

  // Walk forward from today, skipping excluded dates AND any candidate whose end-datetime has
  // already passed (i.e. today's occurrence has ended). If any future, non-excluded, not-yet-
  // ended occurrence exists, the booking is active.
  let candidate = rule.after(todayStart, true);
  while (candidate) {
    if (exceptions.has(utcDayKey(candidate))) {
      candidate = rule.after(candidate, false);
      continue;
    }
    if (endOfBookingDay(candidate) <= now) {
      candidate = rule.after(candidate, false);
      continue;
    }
    return true;
  }
  return false;
}

/**
 * Returns true when the booking has an occurrence on today's date AND the occurrence's end-time
 * has already passed. Used by the "Your bookings" lists to surface a small "bookings today
 * that have ended" table beneath the upcoming list, so users can confirm we haven't silently
 * skipped today's slot.
 */
export function hasTodayExpiredOccurrence(booking: Booking, now: Date = new Date()): boolean {
  if (booking.isCancelled) return false;

  const endHours = parseInt(booking.endTime.slice(0, 2), 10);
  const endMinutes = parseInt(booking.endTime.slice(2, 4), 10);
  // Today's end-of-booking instant, built on `now`'s UTC calendar day (not viewer-local).
  const todayEnd = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), endHours, endMinutes, 0, 0),
  );
  if (todayEnd > now) return false; // booking hasn't finished yet today

  const todayKey = utcDayKey(now);
  // Slot bookings whose `recurrence` document is missing get a synthesised weekly schedule
  // via getEffectiveRecurrence. Without this, isRecurringActive would treat them as
  // non-recurring and report them inactive past their genesis day. See lib/recurrence.ts
  // for the bug write-up.
  const recurrence = getEffectiveRecurrence(booking);

  if (!recurrence) {
    const d = booking.date instanceof Date ? booking.date : new Date(booking.date);
    return utcDayKey(d) === todayKey;
  }

  // Recurring: compute occurrences within today and confirm at least one is non-excluded.
  // dtstart is UTC-midnight of the booking's UTC day — see the comment on the equivalent
  // construction in isRecurringActive above.
  const bookingDate = booking.date instanceof Date ? booking.date : new Date(booking.date);
  const dateStr = utcDayKey(bookingDate);
  const dtStart = new Date(dateStr);
  const rule = new RRule({
    freq:
      recurrence.frequency === "daily"
        ? RRule.DAILY
        : recurrence.frequency === "weekly"
          ? RRule.WEEKLY
          : RRule.MONTHLY,
    interval: recurrence.interval ?? 1,
    count: recurrence.count,
    until: recurrence.until ? new Date(recurrence.until) : undefined,
    byweekday: recurrence.byWeekday
      ? recurrence.byWeekday.map((day) => WEEKDAY_MAP[day])
      : undefined,
    bysetpos: recurrence.bySetPos,
    dtstart: dtStart,
  });
  const { start: dayStart, end: dayEnd } = utcDayBounds(now);
  const occurrences = rule.between(dayStart, dayEnd, true);
  const exceptions = new Set(
    (recurrence.exceptions ?? []).map((d) => utcDayKey(new Date(d))),
  );
  return occurrences.some((occ) => !exceptions.has(utcDayKey(occ)));
}
